import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useViewer } from '@/entities/viewer';
import {
  BoardPreview,
  CardDetailPreview,
  DatabaseCardsPreview,
  FeatureSection,
  FinalCta,
  HowItWorks,
  LandingFooter,
  LandingHeader,
  MonthPreview,
  ProductDemo,
  TeamPreview,
  Window,
} from '@/features/landing';

import styles from './HomePage.module.css';

/**
 * The public landing page (`/`): a header, the hero with the switchable product illustration, one
 * section per capability, teamwork, a short "how to start", the closing call to action and the
 * footer. It shows no account data and fetches nothing but the public service status; the
 * illustrations are static and fictional (see `features/landing/previews.tsx`).
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const viewer = useViewer();
  const signedIn = viewer.status === 'authenticated';

  // The document's title and description follow the language (restored when leaving the page).
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = meta?.content;
    document.title = t('landing.meta.title');
    if (meta) meta.content = t('landing.meta.description');
    return () => {
      document.title = previousTitle;
      if (meta && previousDescription !== undefined) meta.content = previousDescription;
    };
  }, [t, i18n.resolvedLanguage]);

  return (
    <div className={styles.page} id="top">
      <LandingHeader />
      <main className={styles.main}>
        <section className={styles.hero} aria-labelledby="landing-title">
          <h1 id="landing-title" className={styles.headline}>
            <span>{t('landing.hero.title1')}</span> <span>{t('landing.hero.title2')}</span>
          </h1>
          <p className={styles.lead}>{t('landing.hero.lead')}</p>
          <div className={styles.actions}>
            {signedIn ? (
              <Link to="/app" className={styles.primary}>
                {t('landing.cta.open')}
              </Link>
            ) : (
              <>
                <Link to="/register" className={styles.primary}>
                  {t('landing.cta.start')}
                </Link>
                <Link to="/login" className={styles.secondary}>
                  {t('landing.cta.signIn')}
                </Link>
              </>
            )}
          </div>
          <div className={styles.demo}>
            <ProductDemo />
          </div>
        </section>

        <FeatureSection id="boards">
          <Window label={t('landing.demo.desc.boards')}>
            <BoardPreview filters />
          </Window>
          <div className={styles.floating} aria-hidden="true">
            <CardDetailPreview />
          </div>
        </FeatureSection>

        <FeatureSection id="databases" flip>
          <Window label={t('landing.demo.desc.databases')}>
            <DatabaseCardsPreview />
          </Window>
        </FeatureSection>

        <FeatureSection id="calendar">
          <Window label={t('landing.demo.desc.calendar')}>
            <MonthPreview />
          </Window>
        </FeatureSection>

        <FeatureSection id="teamwork" flip>
          <Window label={t('landing.sections.teamwork.text')}>
            <TeamPreview />
          </Window>
        </FeatureSection>

        <HowItWorks />
        <FinalCta />
      </main>
      <LandingFooter />
    </div>
  );
}
